/**
 * AI Strategy Advisor — Interactive chat with a context-aware AI consultant.
 */
import { api, showToast } from '../main.js';

import { t } from '../i18n.js';
import { sanitizeHTML, escapeHTML } from '../sanitize.js';
import { parseMarkdown } from '../utils/markdown.js';

let chatHistory = [];
let isStreaming = false;

export function renderAdvisor(container) {
  container.innerHTML = `
    <div class="page-header">
      <div class="flex justify-between items-center">
        <div>
          <h1 class="page-title">${t('advisor.title')}</h1>
          <p class="page-description">${t('advisor.desc')}</p>
        </div>
        <div id="advisor-context-badge" style="font-size: 0.75rem; color: var(--text-muted); background: rgba(255,255,255,0.03); padding: 6px 12px; border-radius: 8px; border: 1px solid var(--glass-border);">
          ${t('advisor.loadingContext')}
        </div>
      </div>
    </div>

    <div class="card" style="display: flex; flex-direction: column; height: calc(100vh - 160px); overflow: hidden;">
      
      <div id="chat-messages" style="flex: 1; overflow-y: auto; padding: 24px; display: flex; flex-direction: column; gap: 16px;">
        <div class="advisor-msg assistant fade-in">
          <div class="advisor-avatar">🧠</div>
          <div class="advisor-bubble">
            <div class="advisor-bubble-content">
              ${t('advisor.welcomeMsg')}
            </div>
            <div class="advisor-suggestions">
              <button class="advisor-suggestion-btn" data-q="${t('advisor.sugg1q')}">${t('advisor.sugg1')}</button>
              <button class="advisor-suggestion-btn" data-q="${t('advisor.sugg2q')}">${t('advisor.sugg2')}</button>
              <button class="advisor-suggestion-btn" data-q="${t('advisor.sugg3q')}">${t('advisor.sugg3')}</button>
              <button class="advisor-suggestion-btn" data-q="${t('advisor.sugg4q')}">${t('advisor.sugg4')}</button>
            </div>
          </div>
        </div>
      </div>

      <div style="border-top: 1px solid var(--glass-border); padding: 16px 24px; background: var(--bg-secondary);">
        <div style="display: flex; gap: 12px; align-items: flex-end;">
          <textarea id="chat-input" 
            placeholder="${t('advisor.inputPlaceholder')}" 
            rows="1"
            style="flex: 1; resize: none; background: var(--bg-primary); border: 1px solid var(--glass-border); border-radius: 12px; padding: 12px 16px; color: var(--text-primary); font-size: 0.9rem; font-family: inherit; outline: none; transition: border-color 0.2s; max-height: 120px; line-height: 1.5;"
          ></textarea>
          <button id="btn-send" class="btn btn-primary" style="padding: 12px 20px; border-radius: 12px; font-weight: 600; min-width: 80px;">
            ${t('advisor.sendBtn')}
          </button>
        </div>
      </div>
    </div>
  `;

  // Load context badge
  loadContext();

  // Event listeners
  const input = document.getElementById('chat-input');
  const sendBtn = document.getElementById('btn-send');

  sendBtn.addEventListener('click', () => sendMessage());

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  });

  // Auto-resize textarea
  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  });

  // Focus input
  input.addEventListener('focus', () => { input.style.borderColor = 'var(--accent-blue)'; });
  input.addEventListener('blur', () => { input.style.borderColor = 'var(--glass-border)'; });

  // Suggestion buttons
  document.querySelectorAll('.advisor-suggestion-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      input.value = btn.dataset.q;
      sendMessage();
    });
  });
}

async function loadContext() {
  const badge = document.getElementById('advisor-context-badge');
  if (!badge) return;

  try {
    const ctx = await api.get('/advisor/context');
    const parts = [];
    parts.push(t('advisor.contextDims').replace('{dims}', ctx.dimensions).replace('{cps}', ctx.checkpoints));
    if (ctx.has_assessment) parts.push(t('advisor.contextAssLoaded'));
    if (ctx.has_research) parts.push(t('advisor.contextResLoaded'));
    badge.innerHTML = parts.join(' &nbsp;·&nbsp; ');
    badge.style.color = 'var(--accent-emerald)';
  } catch {
    badge.textContent = t('advisor.backendOffline');
    badge.style.color = 'var(--accent-red)';
  }
}

async function sendMessage() {
  const input = document.getElementById('chat-input');
  const msgText = input.value.trim();
  if (!msgText || isStreaming) return;

  input.value = '';
  input.style.height = 'auto';

  // Add user message
  appendMessage('user', msgText);
  chatHistory.push({ role: 'user', content: msgText });

  // Show typing indicator
  const typingEl = appendTypingIndicator();

  isStreaming = true;
  const sendBtn = document.getElementById('btn-send');
  if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = t('advisor.sending'); }

  try {
    const data = await api.post('/api/advisor/chat', {
      message: msgText,
      history: chatHistory.slice(0, -1), // exclude current message
    });
    const reply = data.response;

    // Remove typing indicator
    typingEl.remove();

    // Add assistant response with parsed markdown
    appendMessage('assistant', reply);
    chatHistory.push({ role: 'assistant', content: reply });

  } catch (e) {
    typingEl.remove();
    appendMessage('error', t('advisor.failedResponse').replace('{msg}', e.message));
    showToast(t('advisor.advisorError').replace('{msg}', e.message), 'error');
  } finally {
    isStreaming = false;
    if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = t('advisor.sendBtn'); }
  }
}

function appendMessage(role, content) {
  const container = document.getElementById('chat-messages');
  if (!container) return;

  const div = document.createElement('div');
  div.className = `advisor-msg ${role} fade-in`;

  if (role === 'user') {
    div.innerHTML = `
      <div class="advisor-bubble user-bubble">
        <div class="advisor-bubble-content">${escapeHTML(content)}</div>
      </div>
      <div class="advisor-avatar user-avatar">${t('advisor.youAvatar')}</div>
    `;
  } else if (role === 'error') {
    div.innerHTML = `
      <div class="advisor-avatar" style="background: var(--accent-red);">!</div>
      <div class="advisor-bubble" style="border-color: rgba(239, 68, 68, 0.2);">
        <div class="advisor-bubble-content" style="color: var(--accent-red);">${escapeHTML(content)}</div>
      </div>
    `;
  } else {
    div.innerHTML = `
      <div class="advisor-avatar">🧠</div>
      <div class="advisor-bubble">
        <div class="advisor-bubble-content">${sanitizeHTML(parseMarkdown(content))}</div>
      </div>
    `;
  }

  container.appendChild(div);
  container.scrollTop = container.scrollHeight;
  return div;
}

function appendTypingIndicator() {
  const container = document.getElementById('chat-messages');
  const div = document.createElement('div');
  div.className = 'advisor-msg assistant fade-in';
  div.id = 'typing-indicator';
  div.innerHTML = `
    <div class="advisor-avatar">🧠</div>
    <div class="advisor-bubble">
      <div class="advisor-typing">
        <span class="typing-dot"></span>
        <span class="typing-dot"></span>
        <span class="typing-dot"></span>
      </div>
    </div>
  `;
  container.appendChild(div);
  container.scrollTop = container.scrollHeight;
  return div;
}

